/**
 * rulXAxis.js
 *
 * X-axis utilities for the Remaining Useful Life scatter chart.
 *
 * Data is stored as decimal years (e.g. 2020.54 = mid-July 2020).
 * Default view:  one tick per year   (range > MONTHLY_THRESHOLD)
 * Zoomed-in:     one tick per month  (range ≤ MONTHLY_THRESHOLD)
 */

const MONTH_ABBR = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec",
];

/** years below which we switch from yearly → monthly ticks */
const MONTHLY_THRESHOLD = 1.5;

// ─── Converters ───────────────────────────────────────────────────────────────

/**
 * Converts a Date (or ms timestamp) to a decimal year.
 * e.g. 1 Jul 2020  →  2020.5
 */
export function dateToDecimalYear(date) {
  const d = date instanceof Date ? date : new Date(date);
  return d.getFullYear() + (d.getMonth() + d.getDate() / 31) / 12;
}

/**
 * Formats a decimal year value as "Mon YYYY".
 * Tick positions fall on integer years (2020.0) → "Jan 2020".
 * Sub-year positions (2020.5) → "Jul 2020".
 */
export function decimalYearToLabel(val) {
  if (val == null || !isFinite(val)) return "";
  const year     = Math.floor(val);
  const monthIdx = Math.min(Math.max(Math.round((val - year) * 12), 0), 11);
  return `${MONTH_ABBR[monthIdx]} ${year}`;
}

// ─── Tick amount ──────────────────────────────────────────────────────────────

/**
 * Returns the appropriate tickAmount for a given visible year range.
 *
 * range > MONTHLY_THRESHOLD  →  one tick per year
 * range ≤ MONTHLY_THRESHOLD  →  one tick per month
 *
 * @param {number} min  decimal year
 * @param {number} max  decimal year
 * @returns {number}
 */
export function getRULXAxisTickAmount(min, max) {
  const range = max - min;
  if (range <= 0) return 1;

  if (range > MONTHLY_THRESHOLD) {
    // yearly ticks — one per year in the range
    return Math.max(1, Math.round(range));
  }

  // monthly ticks — one per month in the range
  return Math.max(1, Math.round(range * 12));
}

// ─── Initial axis config ──────────────────────────────────────────────────────

/**
 * Computes the initial X-axis configuration from all data decimal years.
 * Used once on mount / when visible categories change.
 *
 * @param {number[]} decimalYears  all mfgDecimalYear values for visible batteries
 * @returns {{ min: number, max: number, tickAmount: number }}
 */
export function computeRULXAxisConfig(decimalYears) {
  const filtered = (decimalYears || []).filter(
    (v) => typeof v === "number" && isFinite(v)
  );

  if (filtered.length === 0) {
    return { min: 2019, max: 2025, tickAmount: 6 };
  }

  const dataMinYear = Math.floor(Math.min(...filtered));
  const dataMaxYear = Math.floor(Math.max(...filtered));

  const min        = dataMinYear;
  const max        = dataMaxYear + 1;       // one year of right padding
  const tickAmount = getRULXAxisTickAmount(min, max);

  return { min, max, tickAmount };
}
